import { motion } from "framer-motion";
import { Search, Users, Clock, TrendingUp, ArrowUp } from "lucide-react";

const rankingData = [
  { position: 1, clicks: 30 },
  { position: 2, clicks: 20 },
  { position: 3, clicks: 15 },
  { position: 4, clicks: 10 },
  { position: 5, clicks: 8 },
  { position: 6, clicks: 6 },
  { position: 7, clicks: 4 },
  { position: 8, clicks: 3 },
  { position: 9, clicks: 2 },
  { position: 10, clicks: 2 },
];

const stats = [
  {
    icon: Search,
    stat: "90%",
    label: "of people find a local business on Google",
  },
  {
    icon: Users,
    stat: "80%",
    label: "pick one of the top 5 ranking Google Business Profiles",
  },
  {
    icon: Clock,
    stat: "74%",
    label: "of people buy from a Google Business Profile within 24 hours",
  },
];

const searchSections = [
  {
    label: "PPC / Local Service Ads",
    percent: "4%",
    description: "Only 4% of people click on paid ads at the top",
    color: "bg-muted-foreground/20",
    textColor: "text-muted-foreground",
  },
  {
    label: "Local Map Pack (Top 3)",
    percent: "80%",
    description: "80% of clicks go to the top 3 Google Maps results — this is where we get you",
    color: "bg-primary/20",
    textColor: "text-primary",
    highlight: true,
  },
  {
    label: "Organic Website Results",
    percent: "16%",
    description: "Dominated by large directory sites — much harder for local businesses to compete here",
    color: "bg-muted-foreground/20",
    textColor: "text-muted-foreground",
  },
];

export function GbpRankingStats() {
  return (
    <section className="section-padding bg-muted relative overflow-hidden">
      <div className="absolute inset-0 grid-pattern opacity-40" />
      <div className="container-wide relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-10 sm:mb-14"
        >
          <div className="section-badge mb-4">
            <span className="text-xs">Why Rankings Matter</span>
          </div>
          <h2 className="heading-section text-foreground mb-3">
            Where You Rank = <span className="text-gradient">How Many Jobs You Get</span>
          </h2>
          <p className="text-muted-foreground text-base max-w-2xl mx-auto">
            Your position on Google Maps directly affects how many customers find and call you. Here's why moving up in the local Map Pack can transform your enquiry volume.
          </p>
        </motion.div>

        {/* 3 Key Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="grid sm:grid-cols-3 gap-4 max-w-4xl mx-auto mb-12 sm:mb-16"
        >
          {stats.map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.15 + i * 0.1 }}
              className="card-lift bg-card rounded-xl border border-border/60 shadow-card p-5 text-center"
            >
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mx-auto mb-3">
                <item.icon className="w-5 h-5 text-primary" />
              </div>
              <p className="text-3xl sm:text-4xl font-extrabold text-primary mb-1">{item.stat}</p>
              <p className="text-muted-foreground text-sm leading-snug">{item.label}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Where Clicks Go on Google */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl mx-auto mb-12 sm:mb-16"
        >
          <h3 className="text-foreground font-bold text-lg sm:text-xl text-center mb-6">
            Where Do People Click on Google?
          </h3>
          <div className="space-y-3">
            {searchSections.map((section, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 + i * 0.1 }}
                className={`rounded-xl p-4 sm:p-5 border ${
                  section.highlight
                    ? "bg-primary/10 border-primary/30 ring-1 ring-primary/20"
                    : "bg-card border-border/60"
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className={`font-bold text-sm sm:text-base ${section.highlight ? "text-primary" : "text-foreground"}`}>
                    {section.label}
                  </span>
                  <span className={`text-2xl sm:text-3xl font-extrabold ${section.textColor}`}>
                    {section.percent}
                  </span>
                </div>
                <p className="text-muted-foreground text-sm leading-snug">{section.description}</p>
                {section.highlight && (
                  <div className="mt-2 h-2 bg-primary/20 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: "80%" }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.5, duration: 0.8 }}
                      className="h-full bg-primary rounded-full"
                    />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Click Distribution by Ranking */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl mx-auto mb-12 sm:mb-16"
        >
          <h3 className="text-foreground font-bold text-lg sm:text-xl text-center mb-2">
            Click-Through Rate by Google Maps Ranking
          </h3>
          <p className="text-muted-foreground text-sm text-center mb-6">
            Estimated percentage of people who click on each ranking position
          </p>

          <div className="space-y-2.5">
            {rankingData.map((item, i) => (
              <motion.div
                key={item.position}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05, duration: 0.4 }}
                className="flex items-center gap-3"
              >
                <span className={`w-8 text-right font-bold text-sm ${
                  item.position <= 3 ? "text-primary" : "text-muted-foreground"
                }`}>
                  #{item.position}
                </span>
                <div className="flex-1 h-8 bg-muted rounded-lg overflow-hidden relative">
                  <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: `${(item.clicks / 30) * 100}%` }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2 + i * 0.05, duration: 0.6 }}
                    className={`h-full rounded-lg ${
                      item.position <= 3
                        ? "bg-primary"
                        : "bg-muted-foreground/30"
                    }`}
                  />
                </div>
                <span className={`w-12 text-sm font-bold ${
                  item.position <= 3 ? "text-primary" : "text-muted-foreground"
                }`}>
                  {item.clicks}%
                </span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Key Insight Callouts */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl mx-auto"
        >
          <h3 className="text-foreground font-bold text-lg sm:text-xl text-center mb-6">
            What This Means for <span className="text-gradient">Your Business</span>
          </h3>

          <div className="space-y-4">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="card-lift bg-card rounded-xl border border-border/60 shadow-card p-5 sm:p-6"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <ArrowUp className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-foreground font-bold text-sm sm:text-base mb-1">
                    Top of the Map Pack = significantly more enquiries
                  </p>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    Moving from page 2 (around 3% of clicks) into the top 3 of Google Maps (where most clicks happen) can mean a meaningful uplift in calls and enquiries from Google for that keyword. This is why we focus on the keywords that drive real customer demand.
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="card-lift bg-card rounded-xl border border-border/60 shadow-card p-5 sm:p-6"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-foreground font-bold text-sm sm:text-base mb-1">
                    Even small ranking gains add up
                  </p>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    Moving from position 3 to position 1 in the Map Pack typically captures a noticeably higher share of clicks (around 15% &rarr; 30%), which usually translates into more enquiries over time.
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="bg-primary/10 border border-primary/20 rounded-xl p-5 sm:p-6"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-primary text-lg font-extrabold">🚀</span>
                </div>
                <div>
                  <p className="text-foreground font-bold text-sm sm:text-base mb-1">
                    Multiple keywords in the top 3 = compounding growth
                  </p>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    As more of your priority keywords reach the top of the Map Pack, you start receiving enquiries from a much wider range of searches — which usually leads to a steady, compounding lift in calls, bookings and revenue over time.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
